import React, { useContext, useState } from 'react';
import { Box } from '@material-ui/core';
import { useLazyQuery } from '@apollo/client';

import { useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { usePostHog } from 'posthog-js/react';

import { DataSharingInfo } from '../../components/DataSharingInfo/DataSharingInfo';
import { Titles } from '../../components/Titles/Titles';
import { OnboardingFlowContext } from '../../OnboardingFlowContext';
import CoverageRejection from '../CoverageRejection';

import { OverrideToggle } from '../../components/OverrideToggle/OverrideToggle';

import { OnboardingParams } from '../../types';
import { SleepioCoverageRejection } from '../CoverageRejection/SleepioCoverageRejection';
import { ErrorText } from '../../components/ErrorText/ErrorText';

import {
  getPdtUid, getProductId, getProductMinAge, getProductName, getCoverageCopyProduct, isDaylight,
} from '@/utils/productHelpers';

import { Button, TextField } from '@/components';
import {
  getAge, validateDOB, validateZipCode, validateEmployeeId,
} from '@/utils/stringValidators';
import { CHECK_ELIGIBILITY_QUERY } from '@/apollo/queries';
import { formatDateString, normalizeForEligibilityMatch } from '@/utils/stringUtils';

import { getProgram } from '@/utils/posthog';
import { useUtmParams } from '@/hooks/useUtmParams';
import { formatDateInput } from '@/utils/inputHelpers';
import { logError } from '@/utils/logError';

const validateEmployeeIdField = (
  value: string,
  validationRules: {
    validationEnabled?: boolean;
    allowedChars?: string;
    minLength?: number;
    maxLength?: number;
    errorMessage?: string;
  },
  fallbackErrorMessage: string,
): { isValid: boolean; errorMessage: string } => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return {
      isValid: false,
      errorMessage: fallbackErrorMessage,
    };
  }

  const validation = validateEmployeeId(trimmedValue, validationRules);

  return {
    isValid: validation.isValid,
    errorMessage: validation.errorMessage || fallbackErrorMessage,
  };
};

export const CheckCoverage: React.FC = () => {
  const [overrideEligibilityCheck, setOverrideEligibilityCheck] = useState(false);
  const [returnIneligibleMessage, setReturnIneligibleMessage] = useState(false);
  const { product, slug } = useParams<OnboardingParams>();
  const copyProduct = getCoverageCopyProduct(product);

  const {
    next,
    updateFormData,
    onboardingFormData,
    eligibilityFormCustomization: {
      showEmployeeId,
      employeeIdFieldRename,
      showZipCode,
      employeeIdValidationEnabled,
      employeeIdAllowedChars,
      employeeIdMinLength,
      employeeIdMaxLength,
      employeeIdExample,
      employeeIdValidationErrorMessage,
    },
    uid,
  } = useContext(OnboardingFlowContext);
  const [checkEligibility, { loading }] = useLazyQuery(CHECK_ELIGIBILITY_QUERY);
  const [isEligible, setIsEligible] = useState(undefined);
  const { t } = useTranslation();
  const { utmParams } = useUtmParams();

  const posthog = usePostHog();
  const productLine = getProgram(product);

  const posthogCoverageFailed = () => {
    posthog?.capture('CoverageCheckFailed', {
      payer_name: slug,
      'wellness.cortex_uid': uid,
      product_line: productLine,
    });
  };

  // validate user input
  const employeeIdValidation = showEmployeeId
    ? validateEmployeeId(onboardingFormData.employeeId, {
      validationEnabled: employeeIdValidationEnabled,
      allowedChars: employeeIdAllowedChars,
      minLength: employeeIdMinLength,
      maxLength: employeeIdMaxLength,
      errorMessage: employeeIdValidationErrorMessage,
    })
    : { isValid: true };

  const formValidated = onboardingFormData.patientFirstName.trim() !== ''
    && onboardingFormData.patientLastName.trim() !== ''
    && validateDOB(onboardingFormData.patientDateOfBirth.replace(/\s/g, ''))
    && employeeIdValidation.isValid
    && (showZipCode ? validateZipCode(onboardingFormData.patientZipCode) : true);

  // validate each text field individually. used for error states and helper text...
  const [patientFirstNameFieldError, setPatientFirstNameFieldError] = useState(false);
  const [patientLastNameFieldError, setPatientLastNameFieldError] = useState(false);
  const [patientZipcodeFieldError, setPatientZipcodeFieldError] = useState(false);
  const [patientEmployeeIdFieldError, setPatientEmployeeIdFieldError] = useState(false);
  const [patientEmployeeIdFieldErrorMessage, setPatientEmployeeIdFieldErrorMessage] = useState('');
  const [patientDOBFieldError, setPatientDOBFieldError] = useState(false);
  const [patientDOBFieldErrorMessage, setPatientDOBFieldErrorMessage] = useState('');
  const [generalErrorMessage, setGeneralErrorMessage] = useState(undefined);

  const isCaregiver = onboardingFormData.personEnrolling === 'caregiver';
  const employeeIdField = employeeIdFieldRename
    || t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.employee_id_label`);

  const checkCoverage = async () => {
    setGeneralErrorMessage(undefined);
    if (returnIneligibleMessage) {
      posthogCoverageFailed();
      setIsEligible(false);
      return;
    }
    if (overrideEligibilityCheck) {
      next();
      return;
    }
    try {
      const result = await checkEligibility({
        variables: {
          pdtUid: getPdtUid(product),
          firstName: normalizeForEligibilityMatch(onboardingFormData.patientFirstName),
          lastName: normalizeForEligibilityMatch(onboardingFormData.patientLastName),
          dateOfBirth: formatDateString(onboardingFormData.patientDateOfBirth.replace(/\s/g, '')),
          postalCode: onboardingFormData.patientZipCode,
          organizationSlug: slug,
          employeeId: showEmployeeId ? onboardingFormData.employeeId : undefined,
          uid,
          productId: getProductId(product),
          ...utmParams,
        },
      });

      if (result?.data?.eligibilityStatus.isEligible) {
        // happy path, eligible
        next();
      } else if (result?.data?.eligibilityStatus.isEligible === false) {
        // happy path, not eligible
        posthogCoverageFailed();
        logError('[CheckCoverage]', 'coverage check failed', result.error, { level: 'warning' });
        setIsEligible(false);
      } else {
        posthogCoverageFailed();
        logError('[CheckCoverage]', 'eligibility indeterminate', undefined, { level: 'warning' });
        setGeneralErrorMessage(t('coverage.errors.generic_error'));
      }
    } catch (e) {
      // there was a server or browser error
      posthogCoverageFailed();
      logError('[CheckCoverage]', 'browser or server error', e, { level: 'warning' });
      setGeneralErrorMessage(t('coverage.errors.generic_error'));
    }
  };

  if (isEligible === false) {
    const Rejection = product === 'sleepio' || isDaylight(product) ? SleepioCoverageRejection : CoverageRejection;
    return <Rejection isCaregiver={isCaregiver} />;
  }
  return (
    <>
      <Titles
        title={t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.coverage_title`)}
        subtitle={t(
          `coverage.${copyProduct}.${onboardingFormData.personEnrolling}.coverage_subtitle`,
          { productName: getProductName(product) },
        )}
      />
      <Titles
        title={t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.coverage_form_title`)}
      />
      <Box sx={{ marginBottom: 32 }}>
        <TextField
          required
          error={patientFirstNameFieldError}
          helperText={patientFirstNameFieldError ? t('coverage.errors.first_name_error') : ''}
          label={t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.f_name_label`)}
          id="coverageFormFirstName"
          inputProps={{
            placeholder: t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.f_name_label`),
          }}
          value={onboardingFormData.patientFirstName}
          onChange={(e) => {
            setPatientFirstNameFieldError(false);
            updateFormData({ patientFirstName: e.target.value });
          }}
          onBlur={(e) => {
            setPatientFirstNameFieldError(e.target.value.trim() === '');
          }}
        />
        <TextField
          required
          error={patientLastNameFieldError}
          helperText={patientLastNameFieldError ? t('coverage.errors.last_name_error') : ''}
          label={t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.l_name_label`)}
          id="coverageFormLastName"
          inputProps={{
            placeholder: t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.l_name_label`),
          }}
          value={onboardingFormData.patientLastName}
          onChange={(e) => {
            setPatientLastNameFieldError(false);
            updateFormData({ patientLastName: e.target.value });
          }}
          onBlur={(e) => {
            setPatientLastNameFieldError(e.target.value.trim() === '');
          }}
        />
        {showZipCode
          && (
            <TextField
              required
              error={patientZipcodeFieldError}
              helperText={patientZipcodeFieldError ? t('coverage.errors.zipcode_error') : ''}
              label={t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.zipcode_label`)}
              id="coverageFormZipCode"
              inputProps={{
                placeholder: t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.zipcode_label`),
              }}
              value={onboardingFormData.patientZipCode}
              onChange={(e) => {
                setPatientZipcodeFieldError(false);
                updateFormData({ patientZipCode: e.target.value });
              }}
              onBlur={(e) => {
                setPatientZipcodeFieldError(!validateZipCode(e.target.value));
              }}
            />
          )}
        {showEmployeeId && (
          <TextField
            required
            error={patientEmployeeIdFieldError}
            helperText={patientEmployeeIdFieldError ? patientEmployeeIdFieldErrorMessage : ''}
            label={
              employeeIdField
            }
            id="coverageFormEmployeeId"
            inputProps={{
              placeholder: (employeeIdValidationEnabled && employeeIdExample) ? employeeIdExample : employeeIdField,
            }}
            value={onboardingFormData.employeeId}
            onChange={(e) => {
              setPatientEmployeeIdFieldError(false);
              setPatientEmployeeIdFieldErrorMessage('');
              updateFormData({ employeeId: e.target.value });
            }}
            onBlur={(e) => {
              const trimmedValue = e.target.value.trim();
              updateFormData({ employeeId: trimmedValue });

              const { isValid, errorMessage } = validateEmployeeIdField(
                e.target.value,
                {
                  validationEnabled: employeeIdValidationEnabled,
                  allowedChars: employeeIdAllowedChars,
                  minLength: employeeIdMinLength,
                  maxLength: employeeIdMaxLength,
                  errorMessage: employeeIdValidationErrorMessage,
                },
                `${t('coverage.errors.employee_id_error')} ${employeeIdField}`,
              );

              setPatientEmployeeIdFieldError(!isValid);
              setPatientEmployeeIdFieldErrorMessage(errorMessage);
            }}
          />
        )}
        <TextField
          required
          error={patientDOBFieldError}
          helperText={patientDOBFieldErrorMessage}
          label={t(`coverage.${copyProduct}.${onboardingFormData.personEnrolling}.dob_label`)}
          id="coverageFormDOB"
          inputProps={{
            placeholder: 'MM / DD / YYYY',
            maxLength: 14,
            inputMode: 'numeric',
          }}
          value={formatDateInput(onboardingFormData.patientDateOfBirth)}
          onChange={(e) => {
            setPatientDOBFieldError(false);
            setPatientDOBFieldErrorMessage('');
            const digits = e.target.value.replace(/\D/g, '');
            const withSpaces = formatDateInput(digits);
            const compact = withSpaces.replace(/\s/g, '');
            updateFormData({ patientDateOfBirth: compact });
            if (validateDOB(compact)) {
              if (getAge(compact) < getProductMinAge(product)) {
                setPatientDOBFieldErrorMessage(
                  t(
                    `coverage.${copyProduct}.${onboardingFormData.personEnrolling}.dob_age_error`,
                    { productName: getProductName(product) },
                  ),
                );
                setPatientDOBFieldError(true);
              }
            }
          }}
          onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
            e.preventDefault();
            const digits = e.clipboardData.getData('text').replace(/\D/g, '');
            const withSpaces = formatDateInput(digits);
            const compact = withSpaces.replace(/\s/g, '');
            updateFormData({ patientDateOfBirth: compact });
            setPatientDOBFieldError(false);
            setPatientDOBFieldErrorMessage('');
            if (validateDOB(compact)) {
              if (getAge(compact) < getProductMinAge(product)) {
                setPatientDOBFieldErrorMessage(
                  t(
                    `coverage.${copyProduct}.${onboardingFormData.personEnrolling}.dob_age_error`,
                    { productName: getProductName(product) },
                  ),
                );
                setPatientDOBFieldError(true);
              }
            }
          }}
          onBlur={() => {
            const compact = onboardingFormData.patientDateOfBirth;
            if (validateDOB(compact)) {
              if (getAge(compact) < getProductMinAge(product)) {
                setPatientDOBFieldErrorMessage(
                  t(
                    `coverage.${copyProduct}.${onboardingFormData.personEnrolling}.dob_age_error`,
                    { productName: getProductName(product) },
                  ),
                );
                setPatientDOBFieldError(true);
              }
            } else {
              setPatientDOBFieldErrorMessage(t('coverage.errors.dob_error'));
              setPatientDOBFieldError(true);
            }
          }}
        />
        {generalErrorMessage && (
          <ErrorText error={generalErrorMessage} />
        )}
      </Box>
      <Box sx={{ width: '100%', justifyItems: 'center' }}>
        <Button
          onClick={checkCoverage}
          loading={loading}
          disabled={formValidated === false}
          data-testid="submit-button"
          {...(product === 'spark' && { style: { borderRadius: 100 } })}
        >
          {t('coverage.check_coverage')}
        </Button>
      </Box>
      <Box style={{ display: 'flex', justifyContent: 'center' }}>
        <DataSharingInfo />
      </Box>

      {process.env.NODE_ENV !== 'production' && (
        <>
          <OverrideToggle
            userMessage="Override Eligibility Check"
            override={setOverrideEligibilityCheck}
          />
          <OverrideToggle
            userMessage="Force Eligibility Check Failure"
            override={setReturnIneligibleMessage}
          />
        </>
      )}
    </>
  );
};
